import type {
  CreateExpenseDTO,
  ExpenseListQueryDTO,
  ExpenseSummaryQueryDTO,
  UpdateExpenseDTO,
} from "@/modules/expenses/domain/contracts";
import type {
  ExpenseDTO,
  ExpenseListResult,
  ExpenseSummaryDTO,
} from "@/modules/expenses/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

function buildExpensesUrl(path = "", query?: ExpenseListQueryDTO) {
  const url = new URL(`${config.apiUrl}/expenses${path}`);
  if (query?.from) url.searchParams.set("from", query.from);
  if (query?.to) url.searchParams.set("to", query.to);
  if (query?.category) url.searchParams.set("category", query.category);
  if (query?.platform) url.searchParams.set("platform", query.platform);
  if (query?.campaignName) {
    url.searchParams.set("campaignName", query.campaignName);
  }
  if (query?.mappedProductId) {
    url.searchParams.set("mappedProductId", query.mappedProductId);
  }
  if (query?.mappedCategoryId) {
    url.searchParams.set("mappedCategoryId", query.mappedCategoryId);
  }
  if (query?.search) url.searchParams.set("search", query.search);
  if (query?.limit) url.searchParams.set("limit", String(query.limit));
  if (query?.offset) url.searchParams.set("offset", String(query.offset));
  return url;
}

export const expensesApi = {
  async list(query?: ExpenseListQueryDTO): Promise<ExpenseListResult> {
    return fetcher.get(buildExpensesUrl("", query).toString());
  },
  async summary(query: ExpenseSummaryQueryDTO): Promise<ExpenseSummaryDTO> {
    const url = new URL(`${config.apiUrl}/expenses/summary`);
    url.searchParams.set("from", query.from);
    url.searchParams.set("to", query.to);
    if (query.platform) url.searchParams.set("platform", query.platform);
    if (query.campaignName) {
      url.searchParams.set("campaignName", query.campaignName);
    }
    if (query.mappedProductId) {
      url.searchParams.set("mappedProductId", query.mappedProductId);
    }
    if (query.mappedCategoryId) {
      url.searchParams.set("mappedCategoryId", query.mappedCategoryId);
    }
    return fetcher.get(url.toString());
  },
  async get(id: string): Promise<ExpenseDTO> {
    return fetcher.get(`${config.apiUrl}/expenses/${id}`);
  },
  async create(input: CreateExpenseDTO): Promise<ExpenseDTO> {
    return fetcher.post(`${config.apiUrl}/expenses`, input);
  },
  async update(id: string, input: UpdateExpenseDTO): Promise<ExpenseDTO> {
    return fetcher.patch(`${config.apiUrl}/expenses/${id}`, input);
  },
  async delete(id: string): Promise<{ deleted: true; id: string }> {
    return fetcher.delete(`${config.apiUrl}/expenses/${id}`);
  },
  async downloadCsv(query?: ExpenseListQueryDTO): Promise<void> {
    const url = buildExpensesUrl("/export", query);
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new Error(data?.error ?? "ດາວໂຫລດລົ້ມເຫຼວ");
    }
    const blob = await res.blob();
    const filename =
      res.headers.get("Content-Disposition")?.match(/filename="(.+)"/)?.[1] ??
      "expenses.csv";
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  },
};
